import { ChangeDetectionStrategy, Component, ElementRef, Injectable, TemplateRef, Type, inject } from '@angular/core';
import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { FlexibleConnectedPositionStrategy, Overlay } from '@angular/cdk/overlay';
import { AppModalComponent, AppModalShellData } from './app-modal.component';
import {
    APP_MODAL_CONFIRM_CANCEL, APP_MODAL_DATA, APP_MODAL_SAVE_CANCEL, AppModalButton, AppModalConfirmPopup,
    AppModalOptions, AppModalRef, appModalDeleteButtons
} from './app-modal.types';

/** Plain-text body used by the confirm()/confirmDelete() conveniences. */
@Component({
    selector: 'app-modal-text',
    template: '<p class="am-text">{{ text }}</p>',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppModalTextComponent {
    readonly text = String(inject(APP_MODAL_DATA) ?? '');
}

/**
 * Opens AppModal cards (see app-modal.types.ts for the WHY). Usage:
 *
 *   // component content — legacy NgbModal components host unchanged
 *   const ref = this.appModal.open(MarriageComponent, {
 *     size: 'xl', showClose: false, injector: this.injector,
 *   });
 *   ref.result.then(ok => ..., dismissed => ...);
 *
 *   // template content
 *   this.appModal.open(this.myTemplate, { title: 'Details', buttons: APP_MODAL_SAVE_CANCEL });
 *   // in the template: <ng-template let-modal> ... <button (click)="modal.close(x)">
 *
 * Pass `injector: inject(Injector)` from the OPENING screen whenever the
 * content needs route/screen-scoped services (the NgbModal-from-root defect
 * this component exists to end).
 */
@Injectable({ providedIn: 'root' })
export class AppModalService {
    private readonly dialog = inject(Dialog);
    /** Only for `confirmPopup` placement (connected position strategies). */
    private readonly overlay = inject(Overlay);

    /** Every currently-open modal ref — drives {@link hasOpenModal}. */
    private readonly openRefs = new Set<AppModalRef>();
    /** True while a {@link confirmDialog} is showing (singleton guard). */
    private confirmOpen = false;

    /** True while any AppModal card is open (used to gate page shortcuts). */
    hasOpenModal(): boolean {
        return this.openRefs.size > 0;
    }

    open<T = unknown>(content: Type<T> | TemplateRef<unknown>, options: AppModalOptions = {}): AppModalRef<T> {
        const ref = new AppModalRef<T>();
        const popup = options.confirmPopup;
        const positionStrategy = popup ? this.popupPosition(popup) : null;
        const dialogRef = this.dialog.open(AppModalComponent, {
            data: { content, options, ref } as AppModalShellData,
            injector: options.injector,
            // The ref owns the close policy (esc/backdrop below) — CDK must
            // never close underneath it.
            disableClose: true,
            // A popup dims nothing (it is a local affordance, not a modal
            // interruption) but still takes a backdrop so `backdropDismiss`
            // and outside-click behave the same as for a card.
            backdropClass: popup ? 'cdk-overlay-transparent-backdrop' : 'cdk-overlay-dark-backdrop',
            // Anchored popups are placed by the connected strategy below; the
            // centring `.app-modal-pane` rule would fight it (it pins
            // top/left/transform with !important).
            panelClass: popup ? 'app-modal-popup-pane' : 'app-modal-pane',
            ...(positionStrategy ? { positionStrategy } : {}),
        });
        if (positionStrategy) {
            this.trackPopupSide(positionStrategy, dialogRef);
        }
        this.openRefs.add(ref);
        ref.bindTeardown(() => {
            this.openRefs.delete(ref);
            dialogRef.close();
        });
        dialogRef.keydownEvents.subscribe(event => {
            if (event.key === 'Escape' && options.keyboard !== false) {
                ref.dismiss('ESC');
                return;
            }
            // ENTER activates the button that opted in via hotkey: 'enter'
            // (the Confirmation Dialog's Confirm). Skipped inside a TEXTAREA so
            // multiline entry keeps its newline; only opt-in buttons are bound,
            // so ordinary form modals are unaffected.
            if (event.key === 'Enter') {
                const tag = (event.target as HTMLElement | null)?.tagName;
                if (tag === 'TEXTAREA') {
                    return;
                }
                this.activateHotkeyButton(options, 'enter', event, ref);
                return;
            }
            // F1 = the legacy booking-modal Submit key. Content components that
            // handle F1 themselves stop propagation, so this only fires when the
            // key reaches the overlay (e.g. focus parked on the footer bar).
            if (event.key === 'F1') {
                this.activateHotkeyButton(options, 'f1', event, ref);
            }
        });
        dialogRef.backdropClick.subscribe(() => {
            if (options.backdropDismiss) {
                ref.dismiss('BACKDROP_CLICK');
            }
        });
        // Drive the shell's creation pass NOW: legacy NgbModal callers set
        // `ref.componentInstance` fields synchronously after open() — without
        // this the shell's ngOnInit (which creates the content component)
        // only ran on the NEXT change-detection tick and componentInstance
        // was undefined on return (the round-11 continuous-booking break).
        // The content's own ngOnInit stays deferred one microtask (see
        // AppModalComponent) so those assignments land first, like NgbModal.
        dialogRef.componentRef?.changeDetectorRef.detectChanges();
        return ref;
    }

    /**
     * Fires the footer button that opted into `hotkey`, exactly as a click on
     * it would (an `action` button runs the content's logic and owns its own
     * close — see AppModalButton.action).
     */
    private activateHotkeyButton(options: AppModalOptions, hotkey: 'enter' | 'f1',
        event: KeyboardEvent, ref: AppModalRef): void {
        const button = (options.buttons || []).find(b => b.hotkey === hotkey);
        if (!button) {
            return;
        }
        event.preventDefault();
        if (button.action) {
            button.action(ref);
        } else if (button.role === 'dismiss') {
            ref.dismiss(hotkey);
        } else {
            ref.close(button.value);
        }
    }

    /**
     * Connected placement for `confirmPopup`: prefer BELOW the anchor
     * (left-aligned), fall back to ABOVE when the viewport bottom is close.
     * `withPush` keeps a wide panel inside the viewport near the screen edges.
     */
    private popupPosition(popup: AppModalConfirmPopup): FlexibleConnectedPositionStrategy {
        const anchor = popup.anchor instanceof ElementRef ? popup.anchor : new ElementRef(popup.anchor);
        const gap = popup.offset ?? 10;
        return this.overlay.position()
            .flexibleConnectedTo(anchor)
            .withFlexibleDimensions(false)
            .withPush(true)
            .withViewportMargin(8)
            .withPositions([
                { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: gap },
                { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -gap },
                { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: gap },
                { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -gap },
            ]);
    }

    /**
     * Mirrors the APPLIED position onto the card so the arrow points the right
     * way: CDK flips the panel above the anchor when there is no room below,
     * and the arrow has to follow (`am-popup-above`).
     */
    private trackPopupSide(strategy: FlexibleConnectedPositionStrategy, dialogRef: DialogRef<unknown>): void {
        strategy.positionChanges.subscribe(change => {
            const host = dialogRef.componentRef?.location.nativeElement as HTMLElement | undefined;
            if (!host) {
                return;
            }
            // overlayY 'bottom' = the panel's bottom edge sits on the anchor,
            // i.e. the panel is ABOVE it.
            const above = change.connectionPair.overlayY === 'bottom';
            host.classList.toggle('am-popup-above', above);
            host.classList.toggle('am-popup-below', !above);
        });
    }

    /**
     * Text confirm with a caller-defined button bar (default Save/Cancel).
     * Resolves the closing button's `value`; false on any dismissal.
     */
    confirm(title: string, message: string, buttons: readonly AppModalButton[] = APP_MODAL_SAVE_CANCEL,
        options: Omit<AppModalOptions, 'title' | 'data' | 'buttons'> = {}): Promise<unknown> {
        return this.open(AppModalTextComponent, {
            size: 'sm', ...options, title, data: message, buttons,
        }).result.then(value => value, () => false);
    }

    /** Red-Delete confirm; resolves true only when Delete was pressed. */
    confirmDelete(message: string, title = 'Delete', deleteLabel = 'Delete'): Promise<boolean> {
        return this.confirm(title, message, appModalDeleteButtons(deleteLabel)).then(v => v === true);
    }

    /**
     * The 'Confirmation Dialog' classification: standard AppModal chrome
     * (header/footer/✕) + a Close (ESC) / Confirm (ENTER) button bar with
     * keyboard chips and tooltips. Resolves `true` only when Confirm/ENTER is
     * chosen; `false` on Close/ESC/✕/backdrop.
     *
     * SINGLETON by default: while one confirmation is open a repeated call
     * resolves `false` immediately without stacking a second dialog — so a page
     * shortcut that re-fires (e.g. the booking screen's Ctrl = add-to-list) can
     * never pile duplicate confirmations on top of each other (owner-reported
     * 2026-07-18).
     */
    confirmDialog(title: string, message: string,
        options: Omit<AppModalOptions, 'title' | 'data' | 'buttons'> = {}): Promise<boolean> {
        if (this.confirmOpen) {
            return Promise.resolve(false);
        }
        this.confirmOpen = true;
        return this.open(AppModalTextComponent, {
            size: 'sm', ...options, title, data: message,
            buttons: APP_MODAL_CONFIRM_CANCEL, modalClass: 'am-confirm',
        }).result.then(value => value === true, () => false)
            .then(result => {
                this.confirmOpen = false;
                return result;
            });
    }
}
